/**
 * Filters and sorting for the market view.
 *
 * All filtering happens client-side over the already-loaded overview rows, so
 * it's instant. Covers the things people actually screen on: how much they can
 * spend (max price), valuation (P/E range), sector, whether to include
 * funds/ETFs, and which metric to rank by (predicted return, P/E, market cap,
 * etc.), ascending or descending.
 */

import React from "react";
import type { MarketOverviewRow } from "@/types";

export type SortKey =
  | "predicted_return"
  | "price"
  | "pe_ratio"
  | "market_cap"
  | "revenue_growth_yoy"
  | "net_margin"
  | "price_to_book"
  | "roe"
  | "debt_to_equity"
  | "range_position_52w"
  | "confidence";

export interface MarketFilters {
  maxPrice: number | null; // your budget per share
  minPe: number | null;
  maxPe: number | null;
  sector: string; // "" = all
  includeFunds: boolean;
  onlyWithEstimate: boolean;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
}

export const DEFAULT_FILTERS: MarketFilters = {
  maxPrice: null,
  minPe: null,
  maxPe: null,
  sector: "",
  includeFunds: true,
  onlyWithEstimate: false,
  sortKey: "predicted_return",
  sortDir: "desc",
};

const SORT_LABELS: Record<SortKey, string> = {
  predicted_return: "Predicted 1-mo return",
  price: "Share price",
  pe_ratio: "P/E ratio",
  market_cap: "Market cap",
  revenue_growth_yoy: "Revenue growth",
  net_margin: "Net margin",
  price_to_book: "Price / book",
  roe: "Return on equity",
  debt_to_equity: "Debt / equity",
  range_position_52w: "52-wk range position",
  confidence: "Forecast confidence",
};

function sortValue(row: MarketOverviewRow, key: SortKey): number | null {
  switch (key) {
    case "predicted_return":
      return row.outlook_1m?.expected_return_pct ?? null;
    case "confidence":
      return row.outlook_1m?.confidence ?? null;
    case "price":
      return row.price;
    case "pe_ratio":
      return row.pe_ratio;
    case "market_cap":
      return row.market_cap;
    case "revenue_growth_yoy":
      return row.revenue_growth_yoy;
    case "net_margin":
      return row.net_margin;
    case "price_to_book":
      return row.price_to_book;
    case "roe":
      return row.roe;
    case "debt_to_equity":
      return row.debt_to_equity;
    case "range_position_52w":
      return row.range_position_52w;
    default:
      return null;
  }
}

/** Apply the filters + sort to a set of rows (pure, exported for reuse/testing). */
export function applyFilters(
  rows: MarketOverviewRow[],
  filters: MarketFilters,
): MarketOverviewRow[] {
  const filtered = rows.filter((row) => {
    if (!filters.includeFunds && row.instrument_type === "etf_or_fund") return false;
    if (filters.onlyWithEstimate && !row.outlook_1m) return false;
    if (filters.maxPrice !== null && (row.price === null || row.price > filters.maxPrice)) {
      return false;
    }
    if (filters.minPe !== null && (row.pe_ratio === null || row.pe_ratio < filters.minPe)) {
      return false;
    }
    if (filters.maxPe !== null && (row.pe_ratio === null || row.pe_ratio > filters.maxPe)) {
      return false;
    }
    if (filters.sector && row.sector !== filters.sector) return false;
    return true;
  });

  const dir = filters.sortDir === "asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    const av = sortValue(a, filters.sortKey);
    const bv = sortValue(b, filters.sortKey);
    // Nulls always sink to the bottom regardless of direction.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * dir;
  });
}

export default function MarketFilterBar({
  filters,
  onChange,
  sectors,
  resultCount,
  totalCount,
}: {
  filters: MarketFilters;
  onChange: (next: MarketFilters) => void;
  sectors: string[];
  resultCount: number;
  totalCount: number;
}): React.ReactElement {
  const set = (patch: Partial<MarketFilters>): void => onChange({ ...filters, ...patch });
  const numOrNull = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const inputCls =
    "w-24 rounded-lg border border-ink-600 bg-ink-900 px-2 py-1.5 text-sm text-mist-100 placeholder:text-mist-500 focus:border-ink-500 focus:outline-none";
  const selectCls =
    "rounded-lg border border-ink-600 bg-ink-900 px-2 py-1.5 text-sm text-mist-200 focus:border-ink-500 focus:outline-none";

  return (
    <div className="panel space-y-3 p-4">
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-mist-400">Max price (budget/share)</span>
          <div className="flex items-center gap-1">
            <span className="text-mist-500">$</span>
            <input
              type="number"
              min={0}
              value={filters.maxPrice ?? ""}
              onChange={(e) => set({ maxPrice: numOrNull(e.target.value) })}
              placeholder="any"
              className={inputCls}
            />
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-mist-400">P/E from</span>
          <input
            type="number"
            value={filters.minPe ?? ""}
            onChange={(e) => set({ minPe: numOrNull(e.target.value) })}
            placeholder="min"
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-mist-400">P/E to</span>
          <input
            type="number"
            value={filters.maxPe ?? ""}
            onChange={(e) => set({ maxPe: numOrNull(e.target.value) })}
            placeholder="max"
            className={inputCls}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-mist-400">Sector</span>
          <select
            value={filters.sector}
            onChange={(e) => set({ sector: e.target.value })}
            className={selectCls}
          >
            <option value="">All sectors</option>
            {sectors.map((sector) => (
              <option key={sector} value={sector}>
                {sector}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-mist-400">Sort by</span>
          <div className="flex gap-1">
            <select
              value={filters.sortKey}
              onChange={(e) => set({ sortKey: e.target.value as SortKey })}
              className={selectCls}
            >
              {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
                <option key={key} value={key}>
                  {SORT_LABELS[key]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => set({ sortDir: filters.sortDir === "asc" ? "desc" : "asc" })}
              className="rounded-lg border border-ink-600 bg-ink-800 px-2 py-1.5 text-sm text-mist-200 hover:border-ink-500"
              title={filters.sortDir === "asc" ? "Ascending (lowest first)" : "Descending (highest first)"}
            >
              {filters.sortDir === "asc" ? "↑ Low→High" : "↓ High→Low"}
            </button>
          </div>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2 text-mist-300">
          <input
            type="checkbox"
            checked={filters.includeFunds}
            onChange={(e) => set({ includeFunds: e.target.checked })}
            className="accent-signal-teal"
          />
          Include funds/ETFs
        </label>
        <label className="flex items-center gap-2 text-mist-300">
          <input
            type="checkbox"
            checked={filters.onlyWithEstimate}
            onChange={(e) => set({ onlyWithEstimate: e.target.checked })}
            className="accent-signal-teal"
          />
          Only stocks with a forecast
        </label>
        <button
          type="button"
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="text-mist-400 hover:text-mist-200 hover:underline"
        >
          Reset filters
        </button>
        <span className="ml-auto text-mist-500">
          {resultCount} of {totalCount} shown
        </span>
      </div>
    </div>
  );
}
